import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    Radio,
    FormLabel,
    Box,
    Typography,
    List,
    ListItemButton,
    ListItemText,
    CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { apiGet, apiPost } from '@src/services/api/helpers';
import { ContentSearchResultText } from './searchResultDisplay';

const CreateModal = ({ open, onClose, onCreated, initialResource }) => {
    const notify = useNotify();

    const [title, setTitle] = useState('');
    const [bannerType, setBannerType] = useState('standalone');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchPage, setSearchPage] = useState(1);
    const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedResource, setSelectedResource] = useState(null);

    const [states, setStates] = useState([]);
    const [salesChannels, setSalesChannels] = useState([]);
    const [selectedStates, setSelectedStates] = useState([]);
    const [selectedSalesChannels, setSelectedSalesChannels] = useState([]);
    const [homepageEnabled, setHomepageEnabled] = useState(false);
    const [innerpageEnabled, setInnerpageEnabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            const loadStates = apiGet('/states').then((response) => {
                const fetchedStates = response.data.states || [];
                setStates(fetchedStates);
                return fetchedStates;
            }).catch((err) => {
                console.error('Error fetching states:', err);
                return [];
            });

            const loadChannels = apiGet('/sales_channels').then((response) => {
                const fetchedChannels = response.data.sales_channels || [];
                setSalesChannels(fetchedChannels);
                return fetchedChannels;
            }).catch((err) => {
                console.error('Error fetching sales channels:', err);
                return [];
            });

            Promise.all([loadStates, loadChannels]).then(([fetchedStates, fetchedChannels]) => {
                if (initialResource) {
                    setTitle(initialResource.title || '');
                    setBannerType('article_linked');
                    setSelectedResource(initialResource);

                    // Resolve state IDs
                    let resolvedStates = [];
                    if (initialResource.state_ids) {
                        resolvedStates = initialResource.state_ids;
                    } else if (initialResource.states) {
                        resolvedStates = fetchedStates
                            .filter((s) => initialResource.states.includes(s.slug))
                            .map((s) => s.id);
                    }

                    setSelectedStates(resolvedStates);

                    // Resolve sales channel IDs
                    let resolvedChannels = [];
                    if (initialResource.sales_channel_ids) {
                        resolvedChannels = initialResource.sales_channel_ids;
                    } else if (initialResource.sales_channels) {
                        resolvedChannels = fetchedChannels
                            .filter((c) => initialResource.sales_channels.includes(c.slug))
                            .map((c) => c.id);
                    }

                    setSelectedSalesChannels(resolvedChannels);
                } else {
                    setTitle('');
                    setBannerType('standalone');
                    setSelectedResource(null);
                    setSelectedStates([]);
                    setSelectedSalesChannels([]);
                }
            });

            setSearchQuery('');
            setSearchResults([]);
            setSearchPage(1);
            setHasMoreSearchResults(false);
            setIsSearching(false);
            setHomepageEnabled(false);
            setInnerpageEnabled(false);
        }
    }, [open, initialResource]);

    const fetchSearchResults = useCallback(async (query, page, append = false) => {
        setIsSearching(true);
        try {
            const response = await apiGet(
                `/banners/search_resources?query=${encodeURIComponent(query)}&page=${page}`,
            );
            const data = response.data.data || [];
            const hasMore = Boolean(response.data.has_more);

            setSearchResults((prev) => (append ? [...prev, ...data] : data));
            setHasMoreSearchResults(hasMore);
            setSearchPage(page);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSearching(false);
        }
    }, []);

    // Handle content search
    useEffect(() => {
        if (bannerType !== 'article_linked' || !searchQuery) {
            setSearchResults([]);
            setSearchPage(1);
            setHasMoreSearchResults(false);
            return;
        }

        const delayDebounceFn = setTimeout(() => {
            fetchSearchResults(searchQuery, 1, false);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, bannerType, fetchSearchResults]);

    const handleSearchScroll = useCallback((event) => {
        const list = event.currentTarget;
        const isNearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 24;

        if (!isNearBottom || isSearching || !hasMoreSearchResults || !searchQuery) {
            return;
        }

        fetchSearchResults(searchQuery, searchPage + 1, true);
    }, [fetchSearchResults, hasMoreSearchResults, isSearching, searchPage, searchQuery]);

    const handleCreate = async () => {
        if (!title.trim()) {
            notify('Title is required', 'error');
            return;
        }

        if (bannerType === 'article_linked' && !selectedResource) {
            notify('Please select an article to link', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                banner: {
                    title,
                    banner_type: bannerType,
                    homepage_enabled: homepageEnabled,
                    innerpage_enabled: innerpageEnabled,
                    state_ids: selectedStates,
                    sales_channel_ids: selectedSalesChannels,
                },
            };

            if (bannerType === 'article_linked') {
                payload.banner.resource_type = selectedResource.type;
                payload.banner.resource_id = selectedResource.id;
            }

            const response = await apiPost('/banners', payload);
            const newBanner = response.data.banner || Object.values(response.data)[0];
            notify('Banner created successfully', 'info');
            onCreated(newBanner);
            onClose();
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.message || 'Failed to create banner';
            notify(errorMsg, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog fullWidth open={open} maxWidth="sm" onClose={onClose}>
            <DialogTitle>Create New Banner</DialogTitle>
            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={3}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Banner Type</FormLabel>
                        <RadioGroup
                            row
                            value={bannerType}
                            onChange={(e) => {
                                setBannerType(e.target.value);
                                setSelectedResource(null);
                                setSearchQuery('');
                                if (e.target.value === 'article_linked') {
                                    setSelectedStates([]);
                                    setSelectedSalesChannels([]);
                                }
                            }}
                        >
                            <FormControlLabel
                                value="standalone"
                                control={<Radio disabled={Boolean(initialResource)} />}
                                label="Standalone Banner"
                                disabled={Boolean(initialResource)}
                            />
                            <FormControlLabel
                                value="article_linked"
                                control={<Radio disabled={Boolean(initialResource)} />}
                                label="Link to Existing Article"
                                disabled={Boolean(initialResource)}
                            />
                        </RadioGroup>
                    </FormControl>

                    {bannerType === 'article_linked' && (
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: (theme) => theme.palette.mode === 'dark' ? 'divider' : '#e0e0e0',
                                p: 2,
                                borderRadius: 1,
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : '#fcfcfc',
                            }}
                        >
                            <Typography gutterBottom variant="subtitle2">Link Existing Content</Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                                Headline, description, URL, and access scope will be inherited from the selected article.
                                You can override display content per-placement on the edit page.
                            </Typography>
                            {!initialResource && (
                                <TextField
                                    fullWidth
                                    label="Search content..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            )}

                            {selectedResource ? (
                                <Box
                                    sx={{
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : '#e8f0fe',
                                        p: 1.5,
                                        borderRadius: 1,
                                        mt: 2,
                                    }}
                               >
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                        <Box minWidth={0}>
                                            <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                                Selected
                                            </Typography>
                                            <ContentSearchResultText item={selectedResource} />
                                        </Box>
                                        {!initialResource && (
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setSelectedResource(null);
                                                    setSelectedStates([]);
                                                    setSelectedSalesChannels([]);
                                                }}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            ) : (
                                searchResults.length > 0 && (
                                    <List
                                        sx={{
                                            maxHeight: 300,
                                            overflowY: 'auto',
                                            border: '1px solid',
                                            borderColor: (theme) => theme.palette.mode === 'dark' ? 'divider' : '#ddd',
                                            borderRadius: 1,
                                            bgcolor: 'background.paper',
                                            mt: 2,
                                        }}
                                        onScroll={handleSearchScroll}
                                    >
                                        {searchResults.map((item) => (
                                            <ListItemButton
                                                key={`${item.type}-${item.id}`}
                                                onClick={() => {
                                                    setSelectedResource(item);
                                                    setSearchResults([]);
                                                    setHasMoreSearchResults(false);
                                                    setTitle(item.title || '');
                                                    setSelectedStates(item.state_ids || []);
                                                    setSelectedSalesChannels(item.sales_channel_ids || []);
                                                }}
                                            >
                                                <ListItemText
                                                    primary={<ContentSearchResultText item={item} />}
                                                />
                                            </ListItemButton>
                                        ))}
                                        {isSearching ? (
                                            <Box display="flex" justifyContent="center" py={1}>
                                                <CircularProgress size={20} />
                                            </Box>
                                        ) : null}
                                    </List>
                                )
                            )}
                        </Box>
                    )}
                    
                    <TextField
                        required
                        fullWidth
                        label="Banner Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />

                    <Box display="flex" gap={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={homepageEnabled}
                                    onChange={(e) => setHomepageEnabled(e.target.checked)}
                                />
                            }
                            label="Show on Homepage Carousel"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={innerpageEnabled}
                                    onChange={(e) => setInnerpageEnabled(e.target.checked)}
                                />
                            }
                            label="Show on Inner Pages Banner"
                        />
                    </Box>

                    <Box display="flex" gap={2}>
                        <FormControl fullWidth disabled={bannerType === 'article_linked'}>
                            <InputLabel shrink id="states-select-label">States</InputLabel>
                            <Select
                                multiple
                                labelId="states-select-label"
                                value={selectedStates}
                                onChange={(e) => setSelectedStates(e.target.value)}
                            >
                                {states.map((st) => (
                                    <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth disabled={bannerType === 'article_linked'}>
                            <InputLabel shrink id="channels-select-label">Sales Channels</InputLabel>
                            <Select
                                multiple
                                labelId="channels-select-label"
                                value={selectedSalesChannels}
                                onChange={(e) => setSelectedSalesChannels(e.target.value)}
                            >
                                {salesChannels.map((sc) => (
                                    <MenuItem key={sc.id} value={sc.id}>{sc.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button disabled={isSaving} onClick={onClose}>Cancel</Button>
                <Button color="primary" variant="contained" disabled={isSaving} onClick={handleCreate}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateModal;
